import React, { useState, useEffect, useContext } from 'react'
import { connect } from 'react-redux'
import moment from 'moment'

import { 
  getTrainer, fetchTrainer, downloadTrainerProfile, getCourseIndex, fetchCourseIndex
} from '../../../store/client'
import { getUser } from '../../../store/alpha'

import { NavigationContext } from '../../../shared/ui/NavigationController'
import Button from '../../../shared/ui/Button'
import Text from '../../../shared/ui/Text'
import Grid from '../../../shared/ui/Grid'
import Section from '../../../shared/ui/Section'

import CourseCard from './CourseCard'
import ProviderCard from './ProviderCard'

import { withStyles } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import Paper from '@material-ui/core/Paper'
import Avatar from '@material-ui/core/Avatar'
import EducationIcon from '@material-ui/icons/School'
import ExperienceIcon from '@material-ui/icons/Business'
import CloudDownloadOutlinedIcon from '@material-ui/icons/CloudDownloadOutlined'

const styles = theme => ({
  root: {
    position: 'relative'
  },
  sideContent: {
    padding: theme.spacing(3)
  },
  headerTitle: {
    fontSize: theme.typography.h5.fontSize,    
    fontWeight: 600
  },
  headerHeadline: {
    fontSize: theme.typography.h6.fontSize,    
    fontWeight: 300,    
  },
  body: {
    padding: theme.spacing(3)
  },
  experienceTitle: {
    fontSize: theme.typography.body1.fontSize,
    fontWeight: 600,
    color: '#888'
  },
  experiencePlace: {
    fontSize: theme.typography.h6.fontSize,    
    fontWeight: 300    
  },
  experienceDate: {
    fontSize: theme.typography.body1.fontSize,    
    fontWeight: 400,
    color: '#888'
  },
  itemSeparator: {
    height: theme.spacing(2)
  },  
})

const ExperienceItemView = ({ theme, classes, item, icon }) => {
  return (
    <Box display='flex'>
      {/*
      <Box width='2em'>
        { icon }
      </Box>
      */}
      <Box flexGrow={1} display='flex' flexDirection='column' alignItems='stretched'>
        <Box display='flex'>
          <Typography className={classes.experiencePlace}>
            { item.place }
          </Typography>
        </Box>
        <Box display='flex' mt={.2}>
          <Box flexGrow={1}>
            <Typography className={classes.experienceTitle}>
              { item.title }
            </Typography>
          </Box>
          <Box>
            <Typography className={classes.experienceDate}>
              { moment(item.start_date).format('MMM YYYY') }
              { item.end_date ?
                " - " + moment(item.end_date).format('MMM YYYY')
                :
                " - Present"
              }
            </Typography>
          </Box>
        </Box>
        <Box display='flex'>
          <Text text={item.summary} format='markdown' />        
        </Box>
      </Box>
    </Box>
  )
}

const ExperienceListView = ({ theme, classes, data, icon }) => {

  return (
    <Box>
      { data && data.map(item => (
          <ExperienceItemView theme={theme} classes={classes}
            item={item}
            icon={icon}
          />
        )).reduce((p, n) => p ?
          [...p, <Box className={classes.itemSeparator} /> , n] : [n], null
        )        
      }
    </Box>
  )
}

const TrainerDetailView = ({ theme, classes, ...props }) => {

  const { trainerKey, fetchTrainer, fetchCourses, downloadTrainerProfile, isFetching, lastFetched, data, user, courses } = props
  
  const navigationContext = useContext(NavigationContext)

  useEffect(() => {
    fetchTrainer(true)
    fetchCourses()
  }, [])  
    
  const smUp = useMediaQuery(theme.breakpoints.up('sm'))
  
  const getTrainerDetailLink = (row) => {
    return `/trainer/${row.uid}`
  }

  const getCourseDetailLink = (row) => {
    return `/course/${row.uid}`
  }
  
  const getProviderDetailLink = (row) => {
    return `/provider/${row.uid}`
  }
  
  if (!lastFetched)
    return (<></>)
    
  const myCourses = courses.lastFetched ? courses.data.records.filter(c => c.ready_to_sell && c.trainers.find(t => t.uid === parseInt(trainerKey))) : []
  
  return (
    <Paper elevation={2} style={{ overflow: 'hidden' }}>
      <Box display='flex' className={classes.root}>
        <Box style={{ width: '35%' }} className={classes.sideContent}
          display='flex'
          flexDirection='column'
          alignItems='center'
          >
          <Avatar style={{
              width: '180px',
              height: '180px'            
            }}
            src={data.profile_picture_url} />
          <Box mt={4} />
          <Section title="Training Provider" variant="subsection">
            <ProviderCard theme={theme} classes={classes}
              item={data.provider}
              linkTo={getProviderDetailLink(data.provider)}
            />
          </Section>
        </Box>
        <Box flexGrow={1}>
          <Box className={classes.body}>
            <Section gutterBottom>
              <Typography component='h1' className={classes.headerTitle}>
                { data.first_name } { data.last_name}
              </Typography>
              <Typography className={classes.headerHeadline}>
                { data.head_line }
              </Typography>      
              {user.isLoggedIn &&
              <Button style={{marginTop:8}} variant='contained' icon={CloudDownloadOutlinedIcon}
                onClick={() => {
                  navigationContext.showLoadingDialog(true)
                  downloadTrainerProfile(() => navigationContext.showLoadingDialog(false))
                }}>
                Download CV
              </Button>   
              }     
            </Section>
            <Section gutterBottom>
              <Section title="About Me" variant="subsection">
                <Text text={data.summary} format='markdown'/>
              </Section>
            </Section>

            { data.experiences &&
            <Section title="Professional Experiences" gutterBottom>
              <ExperienceListView 
                theme={theme} classes={classes}
                data={data.experiences} 
                icon={<ExperienceIcon />}
              />
            </Section>
            }
            
            { data.experiences &&            
            <Section title="Educations" gutterBottom>
              <ExperienceListView 
                theme={theme} classes={classes}
                data={data.educations} 
                icon={<EducationIcon />}                
              />
            </Section>
            }
            
            <Section title="Course I Teach" gutterBottom>
              <Grid
                size={'medium'} 
                maxRowCount={-1}
                items={myCourses}
                renderItem={(item, index) => (
                <CourseCard theme={theme} classes={classes}
                    item={item}
                    linkTo={getCourseDetailLink(item)}
                    />
                )}
              />
            </Section>            
          </Box>
        </Box>
      </Box>
    </Paper>
  )
}

const mapStateToProps = (state, ownProps) => {
  let trainerKey = ownProps.trainerKey

  return {
    trainerKey,
    ...getTrainer(state,trainerKey),
    user: getUser(state),
    courses: getCourseIndex(state, '', null)
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  let trainerKey = ownProps.trainerKey
  
  return {
    fetchTrainer:() => {
        dispatch(fetchTrainer(trainerKey, true, null))
    },
    fetchCourses: () => {
      dispatch(fetchCourseIndex('', null, true, false))
    },
    downloadTrainerProfile: (onComplete) => {
      dispatch(downloadTrainerProfile(trainerKey, onComplete))
    }
  }
}

export default withStyles(styles, { withTheme: true })(
  connect(
    mapStateToProps,
    mapDispatchToProps
   )(TrainerDetailView)
)
